import React, { useState, useRef, useEffect } from 'react';
import './ApiRunner.css';
import { Send, Trash2 } from 'lucide-react';
import Papa from 'papaparse';
import { authProvider } from '../utils/authProvider';
import Environment from '../utils/Environment';

interface Header {
    key: string;
    value: string;
    isReadonly?: boolean;
}

const ApiRunner = () => {
    const auth = authProvider();
    const userId = auth.userId || 'DXINAZ';
    const [apiUrlPath, setApiUrlPath] = useState('');
    const urlPrefixString = Environment.getSelfServiceAppHost();
    const urlOptions = urlPrefixString.split(',').map(s => s.trim()).filter(s => s);
    const [selectedUrlPrefix, setSelectedUrlPrefix] = useState(urlOptions[0] || '');
    const [headers, setHeaders] = useState<Header[]>([
        { key: 'selectedLocation', value: '', isReadonly: true },
        { key: 'selectedOrganization', value: '', isReadonly: true }
    ]);
    const [body, setBody] = useState('');
    const [response, setResponse] = useState<any | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [activeTab, setActiveTab] = useState('headers');
    const [file, setFile] = useState<File | null>(null);
    const [multiResponse, setMultiResponse] = useState<any[]>([]);
    const [envVars, setEnvVars] = useState<{ key: string; value: string; }[]>([]);
    const [batchProgress, setBatchProgress] = useState<{ processed: number; total: number } | null>(null);
    const [expandedResponses, setExpandedResponses] = useState<Set<number>>(new Set());
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [useSameOrgAsLocation, setUseSameOrgAsLocation] = useState(true);
    const [authToken, setAuthToken] = useState('');

    useEffect(() => {
        if (useSameOrgAsLocation) {
            const newHeaders = [...headers];
            const locationHeader = newHeaders.find(h => h.key === 'selectedLocation');
            const orgHeaderIndex = newHeaders.findIndex(h => h.key === 'selectedOrganization');

            if (locationHeader && orgHeaderIndex !== -1) {
                if (newHeaders[orgHeaderIndex].value !== locationHeader.value) {
                    newHeaders[orgHeaderIndex].value = locationHeader.value;
                    setHeaders(newHeaders);
                }
            }
        }
    }, [headers, useSameOrgAsLocation]);


    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            setFile(e.target.files[0]);
        }
    };

    const handleClearFile = () => {
        setFile(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const toggleResponseExpansion = (index: number) => {
        setExpandedResponses(prev => {
            const newSet = new Set(prev);
            if (newSet.has(index)) {
                newSet.delete(index);
            } else {
                newSet.add(index);
            }
            return newSet;
        });
    };

    const handleSend = async () => {
        // Validate authorization header
        const authHeader = headers.find(header =>
            header.key && header.key.toLowerCase() === 'authorization'
        );

        if (!authHeader || !authHeader.value || authHeader.value.trim() === '') {
            setError('Authorization header is required and cannot be empty. Please add a valid Authorization header.');
            return;
        }

        setLoading(true);
        setError(null);
        setResponse(null);
        setMultiResponse([]);
        setExpandedResponses(new Set());

        const apiEndpoint = Environment.getExecuteBatchAPI();

        if (!apiEndpoint) {
            setError('API endpoint not configured. Please set REACT_APP_EXECUTE_BATCH_API in your environment.');
            setLoading(false);
            return;
        }

        // Create a mutable copy of the URL - don't process here, let backend handle it
        let processedUrl = `${selectedUrlPrefix}${apiUrlPath}`;

        if (file) {
            // Handle CSV data processing and send batch request
            Papa.parse(file, {
                header: true,
                skipEmptyLines: true,
                complete: async (results) => {
                    try {
                        const batchSize = results.data.length;
                        const csvRows = results.data; // Raw CSV rows
                        const csvHeaders = results.meta.fields || []; // CSV headers

                        // Send batch request with raw data to backend
                        const requestHeaders = headers.reduce((acc, header) => {
                            if (header.key) acc[header.key] = header.value;
                            return acc;
                        }, {} as Record<string, string>);

                        // Prepare environment variables for backend
                        const envVariables = envVars.reduce((acc, envVar) => {
                            if (envVar.key) acc[envVar.key] = envVar.value;
                            return acc;
                        }, {} as Record<string, string>);

                        const res = await fetch(apiEndpoint, {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                            },
                            body: JSON.stringify({
                                csvRows: csvRows, // Raw CSV rows
                                csvHeaders: csvHeaders, // CSV headers
                                jsonTemplate: body, // Original JSON template from body (no frontend processing)
                                envVars: envVariables, // Environment variables for backend processing
                                requestUrl: processedUrl, // URL template for backend processing
                                headers: requestHeaders,
                                userId: userId,
                                batchSize: batchSize,
                            }),
                        });

                        if (!res.ok) {
                            const errorText = await res.text();
                            setError(`HTTP error! status: ${res.status} ${res.statusText} - ${errorText}`);
                        } else {
                            // Handle SSE response for batch API execution
                            const reader = res.body?.getReader();
                            const decoder = new TextDecoder();

                            if (reader) {
                                const batchResponses: any[] = [];
                                let totalRequests = 0;
                                let processedCount = 0;

                                try {
                                    while (true) {
                                        const { value, done } = await reader.read();
                                        if (done) break;

                                        const chunk = decoder.decode(value, { stream: true });

                                        // SSE sends "data: {...}"
                                        const lines = chunk.split("\n");
                                        for (const line of lines) {
                                            if (line.startsWith("data:")) {
                                                try {
                                                    const json = JSON.parse(line.replace("data:", "").trim());

                                                    if (json.event === 'start') {
                                                        // Handle batch start event
                                                        totalRequests = json.totalRequests;
                                                        setBatchProgress({ processed: 0, total: totalRequests });
                                                        console.log(`Starting batch processing: ${totalRequests} requests for DC ${json.dcCode}`);

                                                    } else if (json.event === 'result') {
                                                        // Handle individual result
                                                        processedCount = json.processedCount;

                                                        // Update progress
                                                        setBatchProgress({ processed: processedCount, total: totalRequests });

                                                        if (json.error) {
                                                            // Error result
                                                            batchResponses.push({
                                                                error: json.error,
                                                                rowNumber: json.rowNumber,
                                                                index: json.index,
                                                                status: json.status || 'ERROR',
                                                                executionTime: json.executionTime || '0s'
                                                            });
                                                        } else if (json.response) {
                                                            // Success result
                                                            batchResponses.push({
                                                                ...json.response,
                                                                rowNumber: json.rowNumber,
                                                                index: json.index
                                                            });
                                                        }

                                                        // Update UI with current progress
                                                        setMultiResponse([...batchResponses]);

                                                        // Log progress
                                                        console.log(`Processed ${processedCount}/${totalRequests} requests`);

                                                    } else if (json.event === 'complete') {
                                                        // Batch processing completed
                                                        setBatchProgress(null); // Clear progress
                                                        console.log(`Batch processing completed: ${json.totalProcessed}/${json.totalRequests} requests processed`);

                                                    } else if (json.event === 'error') {
                                                        // General error
                                                        setBatchProgress(null); // Clear progress
                                                        setError(`Batch processing error: ${json.message || json}`);
                                                    }

                                                } catch (parseError) {
                                                    console.warn('Failed to parse SSE data line:', line, parseError);
                                                }
                                            }
                                        }
                                    }
                                } catch (streamError) {
                                    console.error('Error reading SSE stream:', streamError);
                                    setError('Error reading server response stream');
                                } finally {
                                    reader.releaseLock();
                                }
                            } else {
                                setError('Unable to read server response stream');
                            }
                        }
                    } catch (e: any) {
                        setError(`Error processing batch request: ${e.message}`);
                    } finally {
                        setLoading(false);
                    }
                }
            });
        } else {
            // If no file is selected, do not proceed with the API call.
            setError('Please select a file to proceed.');
            setLoading(false);
            return;
        }
    };

    const handleAuthTokenChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const token = e.target.value;
        setAuthToken(token);

        const newHeaders = headers.filter(header => header.key.toLowerCase() !== 'authorization');
        if (token) {
            setHeaders([...newHeaders, { key: 'Authorization', value: `Bearer ${token}` }]);
        } else {
            setHeaders(newHeaders);
        }
    };

    const handleAddHeader = () => {
        setHeaders([...headers, { key: '', value: '', isReadonly: false }]);
    };

    const handleRemoveHeader = (index: number) => {
        if (headers[index].isReadonly) return;
        const newHeaders = headers.filter((_, i) => i !== index);
        setHeaders(newHeaders);
    };

    const handleHeaderChange = (index: number, field: 'key' | 'value', value: string) => {
        if (headers[index].isReadonly && field === 'key') return;
        const newHeaders = [...headers];
        const trimmedValue = value.trim();

        if (field === 'key') {
            newHeaders[index][field] = trimmedValue;
        } else { // field === 'value'
            if (newHeaders[index].key === 'selectedLocation' || newHeaders[index].key === 'selectedOrganization') {
                newHeaders[index][field] = trimmedValue.toUpperCase();
            } else {
                newHeaders[index][field] = trimmedValue;
            }
        }
        setHeaders(newHeaders);
    };

    const handleUseSameOrgAsLocationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const isChecked = e.target.checked;
        setUseSameOrgAsLocation(isChecked);

        if (isChecked) {
            const newHeaders = [...headers];
            const locationHeader = newHeaders.find(h => h.key === 'selectedLocation');
            const orgHeaderIndex = newHeaders.findIndex(h => h.key === 'selectedOrganization');

            if (locationHeader && orgHeaderIndex !== -1) {
                newHeaders[orgHeaderIndex].value = locationHeader.value;
                setHeaders(newHeaders);
            }
        }
    };


    const successCount = multiResponse.filter(r => !r.error).length;

    const failedCount = multiResponse.length - successCount;
    const totalRequests = multiResponse.length > 0 ? multiResponse.length : (response ? 1 : 0);

    const handleResetAll = () => {
        setApiUrlPath('');
        setHeaders([
            { key: 'selectedLocation', value: '', isReadonly: true },
            { key: 'selectedOrganization', value: '', isReadonly: true }
        ]);
        setBody('');
        setResponse(null);
        setLoading(false);
        setError(null);
        setActiveTab('headers');
        setFile(null);
        setMultiResponse([]);
        setEnvVars([]);
        setBatchProgress(null);
        setExpandedResponses(new Set());
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
        setAuthToken('');
    };

    return (
        <div className="api-runner-page">
            <h1 className="page-title">API Runner</h1>

            <div className="api-runner-container">
                {/* Left Pane: Request Configuration */}
                <div className="pane request-pane">
                    <div className="url-bar">
                        <div className="method-dropdown">POST</div>
                        <div className="url-input-container">
                            <select
                                value={selectedUrlPrefix}
                                onChange={(e) => setSelectedUrlPrefix(e.target.value)}
                                className="url-prefix-select"
                            >
                                {urlOptions.map((option, index) => (
                                    <option key={index} value={option}>{option}</option>
                                ))}
                            </select>
                            <input
                                type="text"
                                value={apiUrlPath}
                                onChange={(e) => setApiUrlPath(e.target.value)}
                                onFocus={(e) => {
                                    if (!apiUrlPath.startsWith('/')) {
                                        setApiUrlPath('/' + apiUrlPath);
                                    }
                                }}
                                placeholder="Enter request path"
                                className="url-input"
                            />
                        </div>
                        <button onClick={handleSend} disabled={loading} className="send-button">
                            {loading ? <div className="loader" /> : <Send size={18} />}
                            <span>{loading ? 'Sending...' : 'Send'}</span>
                        </button>
                    </div>

                    <div className="details-tabs">
                        <button onClick={() => setActiveTab('headers')} className={activeTab === 'headers' ? 'active' : ''}>Headers</button>
                        <button onClick={() => setActiveTab('body')} className={activeTab === 'body' ? 'active' : ''}>Body</button>
                        <button onClick={() => setActiveTab('auth')} className={activeTab === 'auth' ? 'active' : ''}>Authorization</button>
                    </div>

                    <div className="details-content">
                        {activeTab === 'headers' && (
                            <div className="key-value-editor">
                                {headers.map((header, index) => (
                                    <div key={index} className="key-value-pair">
                                        <input type="text" placeholder="Key" value={header.key} onChange={(e) => handleHeaderChange(index, 'key', e.target.value)} readOnly={header.isReadonly} />
                                        <input type="text" placeholder="Value" value={header.value} onChange={(e) => handleHeaderChange(index, 'value', e.target.value)} />
                                        {!header.isReadonly && <button onClick={() => handleRemoveHeader(index)} className="remove-btn"><Trash2 size={16} /></button>}
                                        {header.isReadonly && <div className="readonly-placeholder" />}
                                    </div>
                                ))}
                                <div className="header-controls">
                                    <button onClick={handleAddHeader} className="add-btn">Add Header</button>
                                    <div className="sync-org-checkbox">
                                        <input
                                            type="checkbox"
                                            id="sync-org"
                                            checked={useSameOrgAsLocation}
                                            onChange={handleUseSameOrgAsLocationChange}
                                        />
                                        <label htmlFor="sync-org">Sync Org with Location</label>
                                    </div>
                                </div>
                            </div>
                        )}
                        {activeTab === 'body' && (
                            <textarea
                                value={body}
                                onChange={(e) => setBody(e.target.value)}
                                placeholder='{ "key": "value" }'
                                className="body-input"
                            />
                        )}
                        {activeTab === 'auth' && (
                            <div className="auth-section">
                                <input
                                    type="text"
                                    placeholder="Enter Bearer Token"
                                    value={authToken}
                                    onChange={handleAuthTokenChange}
                                    className="auth-token-input"
                                />
                            </div>
                        )}
                    </div>
                     <button onClick={handleResetAll} className="reset-link">
                        <Trash2 size={16} />
                        <span>Reset</span>
                    </button>
                </div>

                {/* Right Pane: Response */}
                <div className="pane response-pane">
                    <div className="response-header">
                        <h2 className="pane-title">Response {totalRequests > 0 && `(${totalRequests})`}</h2>
                        {batchProgress && (
                            <div className="batch-progress">
                                <div className="progress-text">
                                    Processing: {batchProgress.processed}/{batchProgress.total} requests
                                </div>
                                <div className="progress-bar">
                                    <div
                                        className="progress-fill"
                                        style={{
                                            width: `${(batchProgress.processed / batchProgress.total) * 100}%`
                                        }}
                                    />
                                </div>
                            </div>
                        )}
                        {multiResponse.length > 0 && (
                            <div className="response-summary">
                                <span className="success-count">Success: {successCount}</span>
                                <span className="failed-count">Failed: {failedCount}</span>
                            </div>
                        )}
                    </div>
                    <div className="response-content">
                        {loading && <div className="loading-overlay"><div className="loader" /><span>Loading...</span></div>}
                        {error && <pre className="error-response">{error}</pre>}
                        {response && (
                            <div className="single-response-container">
                                <div className="response-meta">
                                    {(() => {
                                        // Handle both old and new response structures
                                        const responseBody = response.response;
                                        const status = responseBody?.[0]?.status || response.status || responseBody?.status || 'ERROR';
                                        const isSuccess = status === 'SUCCESS';
                                        const executionTime = response.timeTaken || response.executionTime || responseBody?.[0]?.executionTime || responseBody?.executionTime || '0s';
                                        
                                        // Clone and modify the response to remove executionTime and status
                                        let cleanedResponse;
                                        if (responseBody && Array.isArray(responseBody)) {
                                            cleanedResponse = JSON.parse(JSON.stringify(responseBody));
                                            cleanedResponse.forEach((item: any) => {
                                                delete item.executionTime;
                                                delete item.status;
                                            });
                                        } else if (responseBody) {
                                            cleanedResponse = JSON.parse(JSON.stringify(responseBody));
                                            delete cleanedResponse.executionTime;
                                            delete cleanedResponse.status;
                                        } else {
                                            cleanedResponse = response;
                                            delete cleanedResponse.executionTime;
                                            delete cleanedResponse.status;
                                            delete cleanedResponse.timeTaken;
                                        }

                                        return (
                                            <>
                                                <span className={`status-code ${isSuccess ? 'success' : 'error'}`}>
                                                    Status: {status}
                                                </span>
                                                <span className="execution-time">Time taken: {executionTime} s</span>
                                            </>
                                        );
                                    })()}
                                </div>
                                <pre className="response-body" style={{ overflowX: 'auto' }}>
                                    {JSON.stringify(JSON.parse(JSON.stringify(response.response || response, (key, value) => {
                                        if (key === 'executionTime' || key === 'status' || key === 'timeTaken') return undefined;
                                        return value;
                                    })), null, 2)}
                                </pre>
                            </div>
                        )}
                        {multiResponse.length > 0 && (
                            <div>
                                {multiResponse.map((res, index) => {
                                    // Handle both nested response structure and direct properties
                                    const executionTime = res.executionTime || res.response?.executionTime || '0s';
                                    const status = res.status || res.response?.status || 'UNKNOWN';
                                    const isSuccess = status === 'SUCCESS';
                                    const hasError = res.error || res.response?.error;

                                    // Prepare a cleaned response for display - only show requestPayload value
                                    let cleanedDisplayResponse;
                                    if (hasError) {
                                        cleanedDisplayResponse = { error: res.error || res.response?.error };
                                    } else {
                                        // Only show the requestPayload value directly
                                        const requestPayload = res.requestPayload || res.response?.requestPayload;
                                        if (requestPayload) {
                                            cleanedDisplayResponse = requestPayload;
                                        } else {
                                            cleanedDisplayResponse = { message: "No request payload available" };
                                        }
                                    }

                                    return (
                                        <div key={index} className="response-item">
                                            <div className="response-header" onClick={() => toggleResponseExpansion(index)}>
                                                <span className="response-row">Row {res.rowNumber}</span>
                                                <span className="response-time">{executionTime}</span>
                                                <span className={`response-message ${isSuccess ? 'success' : 'error'}`}>
                                                    {hasError ? 'Error' : status}
                                                </span>
                                            </div>
                                            {expandedResponses.has(index) && (
                                                <div className="response-body" style={{ overflowX: 'auto' }}>
                                                    <pre>{JSON.stringify(cleanedDisplayResponse, null, 2)}</pre>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                                
                            </div>
                        )}
                        {!loading && !error && !response && multiResponse.length === 0 && <div className="no-response">Click "Send" to get a response</div>}
                    </div>
                    
                </div>
                
            </div>

            {/* Bottom Section for File Upload and Env Vars */}
            <div className="bottom-section">
                <div className="options-card">
                    <h3><span role="img" aria-label="file">📄</span> File Upload (CSV)</h3>
                    <p>Upload a CSV file to run requests in a batch.</p>
                    <div className="file-input-container">
                        <input type="file" accept=".csv" onChange={handleFileChange} ref={fileInputRef} />
                        {file && <button onClick={handleClearFile} className="clear-file-btn">Clear</button>}
                    </div>
                </div> 
            </div>
        </div>
    );
};

export default ApiRunner;